// Browser-safe public API. Node-only helpers (loadManifest +
// validateManifest, which use fs/js-yaml) live in ./node.ts and are
// only imported by the generator script — never bundled for the
// browser.

export {
  ServiceManifestSchema,
  type ServiceManifest,
  type NavPlacement,
  type ResourceDef,
  type ToolDef,
  type ExportDef,
  type Constraint,
} from './schema.js';
export { MANIFESTS } from './manifests.generated.js';

import type { ServiceManifest } from './schema.js';
import { MANIFESTS } from './manifests.generated.js';

/** Lookup a manifest by service id. Returns null when the service isn't registered. */
export function manifestFor(serviceId: string): ServiceManifest | null {
  return MANIFESTS[serviceId] ?? null;
}

export interface RegistrySubItem {
  id: string;
  label: string;
  /** Absolute href, derived from `/{service}/{kind-or-tool-id}`. */
  href: string;
}

/**
 * Returns the nav sub-items a section should display, derived from its
 * manifest's `resources[]` (one entry per resource → /{service}/{plural})
 * and `tools[]` (one entry per tool → /{service}/{tool.id}).
 *
 * Returns [] when no manifest is registered for that section id.
 */
export function getSubItemsForSection(serviceId: string): RegistrySubItem[] {
  const m = MANIFESTS[serviceId];
  if (!m) return [];
  const items: RegistrySubItem[] = [];
  for (const r of m.resources) {
    // Both the label and the href use the plural form so they match the
    // List screen route (e.g. `User` → label "Users", href `/iam/users`).
    const pluralSegment = toUrlSegment(pluralize(r.name));
    items.push({
      id: `${serviceId}-${pluralSegment}`,
      label: pluralize(r.name),
      href: `/${serviceId}/${pluralSegment}`,
    });
  }
  for (const t of m.tools) {
    items.push({
      id: `${serviceId}-${t.id}`,
      label: t.label,
      href: `/${serviceId}/${toUrlSegment(t.id)}`,
    });
  }
  return items;
}

function toUrlSegment(s: string): string {
  return s
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .toLowerCase();
}

function pluralize(name: string): string {
  if (/s$|x$|ch$|sh$/.test(name)) return name + 'es';
  if (/[^aeiou]y$/.test(name)) return name.slice(0, -1) + 'ies';
  return name + 's';
}
